/**
 * Keeps shopify-account menu labels and account portal links on English
 * when the component loads Chinese strings from browser/customer language detection.
 */
const ORDERS_ALIASES = new Set(['订单', '訂單', 'Orders']);
const PROFILE_ALIASES = new Set(['资料', '个人资料', '個人檔案', 'Profile']);

function withAccountLocale(url, locale = 'en-US', regionCountry = 'US') {
  if (!url || url.startsWith('#') || url.startsWith('javascript:')) return url;

  try {
    const absolute = new URL(url, window.location.href);
    const isAccountUrl =
      absolute.pathname.includes('customer_authentication') ||
      absolute.hostname.startsWith('account.') ||
      (absolute.hostname === 'shopify.com' && absolute.pathname.includes('/authentication/'));

    if (!isAccountUrl) return url;

    absolute.searchParams.set('locale', locale);
    absolute.searchParams.set('region_country', regionCountry);
    return absolute.href;
  } catch {
    return url;
  }
}

class ShopifyAccountLocale extends HTMLElement {
  #observer;

  connectedCallback() {
    this.#bindAccount(document.querySelector('shopify-account'));
    document.addEventListener('shopify:section:load', () => {
      this.#bindAccount(document.querySelector('shopify-account'));
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #bindAccount(account) {
    if (!account || account.dataset.localeBound === 'true') return;
    account.dataset.localeBound = 'true';

    const ordersLabel = account.dataset.labelOrders || 'Orders';
    const profileLabel = account.dataset.labelProfile || 'Profile';
    const accountLocale = account.dataset.accountLocale || 'en-US';
    const accountRegion = account.dataset.accountRegion || 'US';

    const patch = () => this.#patchAccount(account, ordersLabel, profileLabel, accountLocale, accountRegion);

    customElements.whenDefined('shopify-account').then(() => {
      let clickBound = false;

      const start = () => {
        patch();
        const root = account.shadowRoot;
        if (!root) {
          window.requestAnimationFrame(start);
          return;
        }

        this.#observer?.disconnect();
        this.#observer = new MutationObserver(patch);
        this.#observer.observe(account, { childList: true, subtree: true });
        this.#observer.observe(root, { childList: true, subtree: true });

        if (!clickBound) {
          clickBound = true;
          root.addEventListener(
            'click',
            (event) => {
              const link = event.composedPath().find((node) => node instanceof HTMLAnchorElement && node.href);
              if (!link) return;

              const patched = withAccountLocale(link.href, accountLocale, accountRegion);
              if (!patched || patched === link.href) return;

              event.preventDefault();
              event.stopPropagation();
              window.location.assign(patched);
            },
            true
          );
        }
      };

      start();
      account.addEventListener('open', patch);
      account.addEventListener('toggle', patch, true);
    });
  }

  #patchAccount(account, ordersLabel, profileLabel, accountLocale, accountRegion) {
    const root = account.shadowRoot;
    if (!root) return;

    root.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      const patched = withAccountLocale(href, accountLocale, accountRegion);
      if (patched && patched !== href) {
        link.setAttribute('href', patched);
      }
    });

    root.querySelectorAll('a, button, span, p').forEach((node) => {
      const text = node.textContent?.trim();
      if (!text) return;

      if (ORDERS_ALIASES.has(text) && text !== ordersLabel) {
        node.textContent = ordersLabel;
      } else if (PROFILE_ALIASES.has(text) && text !== profileLabel) {
        node.textContent = profileLabel;
      }
    });
  }
}

if (!customElements.get('shopify-account-locale')) {
  customElements.define('shopify-account-locale', ShopifyAccountLocale);
}
